import type { Option } from "../core/option.ts";

/**
 * A row type's wire name — what a new row is created as (`"number"`,
 * `"checkmark"`, `"tasks"`, a type an app registered): the `type` of
 * `handle.rows.add({ type, fields, lookupKey }, actor)`. A string, but only
 * `RowTypeName.parse` (`null` for text that names no type) and
 * `RowTypeName.of` (throws) make one, so a row's other text (its label, its
 * lookup key) cannot be passed where its type is meant.
 */
export type RowTypeName = string & { readonly __brand: "RowTypeName" };

/** Values that go with `RowTypeName`: `parse`, `of`. */
export namespace RowTypeName {
  /** `raw` as a row type name, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<RowTypeName> {
    return raw.length > 0 ? mint(raw) : null;
  }

  /** `raw` as a row type name; throws when it is not one (an empty string). */
  export function of(raw: string): RowTypeName {
    const name = parse(raw);
    if (name == null) throw new TypeError(`RowTypeName.of: "${raw}" is not a row type name`);
    return name;
  }
}

/** The one place a string becomes a `RowTypeName`, after `parse` checked it. */
function mint(raw: string): RowTypeName {
  return raw as RowTypeName;
}
